import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { QRCodeSVG } from "qrcode.react";
import { Html5QrcodeScanner } from "html5-qrcode";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from "date-fns";
import {
  ForkKnife, ArrowLeft, Users, CalendarBlank, ChartBar, 
  Coffee, Sun, Cookie, Moon, Plus, FileText, QrCode, Camera,
  CheckCircle, Warning, UserPlus, MagnifyingGlass
} from "@phosphor-icons/react";

const MEAL_TYPES = [
  { id: "breakfast", label: "Breakfast", icon: Coffee, color: "bg-orange-100 text-orange-700", time: "7:00 - 9:30 AM" },
  { id: "lunch", label: "Lunch", icon: Sun, color: "bg-yellow-100 text-yellow-700", time: "12:00 - 2:00 PM" },
  { id: "snack", label: "Snack", icon: Cookie, color: "bg-pink-100 text-pink-700", time: "4:00 - 5:00 PM" },
  { id: "dinner", label: "Dinner", icon: Moon, color: "bg-indigo-100 text-indigo-700", time: "7:00 - 9:00 PM" },
];

// HTML-escape user data before injecting into a document.write payload.
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Lightweight WebAudio beep — no external assets needed.
// type: "success" = double high beep, "error" = single low buzz.
function playBeep(type = "success") {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const beep = (freq, start, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + duration);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + duration + 0.02);
    };
    if (type === "success") {
      beep(880, 0, 0.12);
      beep(1320, 0.13, 0.18);
    } else {
      beep(220, 0, 0.35);
    }
    setTimeout(() => ctx.close().catch(() => {}), 800);
  } catch (e) {
    // Audio failed (e.g., autoplay policy) — silently ignore
  }
}

export default function CanteenManagement() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Date selection
  const [selectedDate, setSelectedDate] = useState(new Date());
  const formattedDate = format(selectedDate, "yyyy-MM-dd");
  
  // Indent form
  const [indent, setIndent] = useState({
    employee_checkin_count: 0,
    estimates: {
      breakfast: { employee: 0, guest: 0 },
      lunch: { employee: 0, guest: 0 },
      snack: { employee: 0, guest: 0 },
      dinner: { employee: 0, guest: 0 },
    }
  });
  
  // Counts form
  const [counts, setCounts] = useState({
    breakfast: { employee: 0, guest: 0 },
    lunch: { employee: 0, guest: 0 },
    snack: { employee: 0, guest: 0 },
    dinner: { employee: 0, guest: 0 },
  });
  
  // QR Scanner
  const [scannerActive, setScannerActive] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState("lunch");
  const [lastCheckin, setLastCheckin] = useState(null);
  const [todayCheckins, setTodayCheckins] = useState(null);
  const scannerRef = useRef(null);
  // Prevents the html5-qrcode scanner (running at fps=10) from firing the same QR multiple times
  const processingRef = useRef(false);
  const lastScanRef = useRef({ code: null, at: 0 });
  // Throttle: only refresh dashboard/checkins at most every 4s during rapid scanning
  const lastRefreshRef = useRef(0);
  
  // Employee QRs
  const [employeeQRs, setEmployeeQRs] = useState([]);
  const [newEmployeeDialog, setNewEmployeeDialog] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ first_name: "", last_name: "" });
  const [viewQRDialog, setViewQRDialog] = useState(null);
  const [employeeQRsSearch, setEmployeeQRsSearch] = useState("");
  const filteredEmployeeQRs = useMemo(() => {
    const q = employeeQRsSearch.trim().toLowerCase();
    if (!q) return employeeQRs;
    return employeeQRs.filter((e) =>
      (e.employee_name || "").toLowerCase().includes(q) ||
      (e.employee_email || "").toLowerCase().includes(q) ||
      (e.department || "").toLowerCase().includes(q) ||
      (e.qr_code || "").toLowerCase().includes(q)
    );
  }, [employeeQRs, employeeQRsSearch]);

  // Bulk import
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkCsvText, setBulkCsvText] = useState("");
  const [bulkPreview, setBulkPreview] = useState([]);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  
  // Guest check-in
  const [guestDialog, setGuestDialog] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestCount, setGuestCount] = useState(1);
  
  // Reports
  const [reports, setReports] = useState([]);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportType, setReportType] = useState("daily");
  const [reportDateRange, setReportDateRange] = useState({ start: null, end: null });
  const [generatingReport, setGeneratingReport] = useState(false);
  const [viewingReport, setViewingReport] = useState(null);

  // Drill-down for meal type inside a viewed report
  const [drillDown, setDrillDown] = useState(null); // { meal, label, employees, guests, loading }

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/canteen/dashboard`, { withCredentials: true });
      setDashboard(response.data);
    } catch (error) {
      // NEVER auto-logout from the scanner loop — a single transient 401 (proxy blip,
      // rate-limit page, expired session on one specific request) would boot the operator
      // mid-queue after ~40 rapid scans. Just log; the actual checkin request will surface
      // a real auth error to the user if needed.
      console.error("Error fetching dashboard:", error);
    }
  }, []);

  // Fetch indent for selected date
  const fetchIndent = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/canteen/indent/${formattedDate}`, { withCredentials: true });
      setIndent({
        employee_checkin_count: response.data.employee_checkin_count || 0,
        estimates: response.data.estimates || {
          breakfast: { employee: 0, guest: 0 },
          lunch: { employee: 0, guest: 0 },
          snack: { employee: 0, guest: 0 },
          dinner: { employee: 0, guest: 0 },
        }
      });
    } catch (error) {
      console.error("Error fetching indent:", error);
    }
  }, [formattedDate]);

  // Fetch counts for selected date
  const fetchCounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/canteen/counts/${formattedDate}`, { withCredentials: true });
      const countsData = response.data.counts || {};
      setCounts({
        breakfast: countsData.breakfast || { employee_count: 0, guest_count: 0 },
        lunch: countsData.lunch || { employee_count: 0, guest_count: 0 },
        snack: countsData.snack || { employee_count: 0, guest_count: 0 },
        dinner: countsData.dinner || { employee_count: 0, guest_count: 0 },
      });
    } catch (error) {
      console.error("Error fetching counts:", error);
    }
  }, [formattedDate]);

  // Fetch employee QRs — pulls from the system Users list (single source of truth)
  const fetchEmployeeQRs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/canteen/users`, { withCredentials: true });
      // Normalize each system user into the shape the QR table & dialog expect
      const mapped = (response.data || []).map((u) => {
        const parts = (u.name || "").trim().split(/\s+/);
        return {
          qr_id: u.user_id,
          user_id: u.user_id,
          first_name: parts[0] || "",
          last_name: parts.slice(1).join(" ") || "",
          employee_name: u.name || "",
          employee_email: u.email || "",
          department: u.department || "",
          qr_code: u.qr_code || "",
          training_role: u.training_role || "employee",
        };
      });
      setEmployeeQRs(mapped);
    } catch (error) {
      console.error("Error fetching users for QR:", error);
    }
  }, []);

  // Fetch today's checkins
  const fetchTodayCheckins = useCallback(async () => {
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const response = await axios.get(`${API}/canteen/checkins/${today}`, { withCredentials: true });
      setTodayCheckins(response.data);
    } catch (error) {
      console.error("Error fetching checkins:", error);
    }
  }, []);

  // Fetch reports
  const fetchReports = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/canteen/reports`, { withCredentials: true });
      setReports(response.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchReports(), fetchEmployeeQRs()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchReports, fetchEmployeeQRs]);

  useEffect(() => {
    if (activeTab === "indent") {
      fetchIndent();
    } else if (activeTab === "counts") {
      fetchCounts();
    } else if (activeTab === "scanner") {
      fetchTodayCheckins();
    }
  }, [activeTab, formattedDate, fetchIndent, fetchCounts, fetchTodayCheckins]);

  // Initialize QR Scanner
  useEffect(() => {
    if (activeTab === "scanner" && scannerActive && !scannerRef.current) {
      const scanner = new Html5QrcodeScanner("qr-reader", {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      });

      scanner.render(
        async (decodedText) => {
          // Debounce: ignore if same code scanned within last 4s OR a request is in flight
          const now = Date.now();
          if (processingRef.current) return;
          if (
            lastScanRef.current.code === decodedText &&
            now - lastScanRef.current.at < 4000
          ) {
            return;
          }
          processingRef.current = true;
          lastScanRef.current = { code: decodedText, at: now };

          // Pause the camera so it stops re-decoding while we process + show result
          try { await scanner.pause(true); } catch (e) { console.debug("swallowed error:", e); }

          try {
            const response = await axios.post(`${API}/canteen/checkin`, {
              qr_code: decodedText,
              meal_type: selectedMealType
            }, { withCredentials: true });

            setLastCheckin({
              success: true,
              message: response.data.message,
              employee: response.data.checkin
            });
            playBeep("success");
            toast.success(response.data.message);
            // Throttle refresh: at most once every 4 seconds during a rapid scanning queue
            const nowMs = Date.now();
            if (nowMs - lastRefreshRef.current > 4000) {
              lastRefreshRef.current = nowMs;
              fetchDashboard();
              fetchTodayCheckins();
            }
          } catch (error) {
            const errorMsg = error.response?.data?.detail || "Check-in failed";
            setLastCheckin({ success: false, message: errorMsg });
            playBeep("error");
            toast.error(errorMsg);
          } finally {
            // Auto-resume scanning after a short pause so the next badge can be scanned
            setTimeout(() => {
              if (scannerRef.current) {
                try { scannerRef.current.resume(); } catch (e) { console.debug("swallowed error:", e); }
              }
              processingRef.current = false;
            }, 2200);
          }
        },
        () => {
          // Frame decode error — ignored (normal during scanning)
        }
      );

      scannerRef.current = scanner;
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(console.error);
        scannerRef.current = null;
      }
      processingRef.current = false;
      lastScanRef.current = { code: null, at: 0 };
    };
  }, [activeTab, scannerActive, selectedMealType, fetchDashboard, fetchTodayCheckins]);

  // Save indent
  const saveIndent = async () => {
    try {
      await axios.post(`${API}/canteen/indent`, {
        date: formattedDate,
        employee_checkin_count: indent.employee_checkin_count,
        estimates: indent.estimates
      }, { withCredentials: true });
      toast.success("Indent saved successfully");
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save indent");
    }
  };

  // Save meal count
  const saveMealCount = async (mealType) => {
    try {
      const count = counts[mealType];
      await axios.post(`${API}/canteen/count`, {
        date: formattedDate,
        meal_type: mealType,
        employee_count: count.employee_count || count.employee || 0,
        guest_count: count.guest_count || count.guest || 0
      }, { withCredentials: true });
      toast.success(`${mealType.charAt(0).toUpperCase() + mealType.slice(1)} count saved`);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save count");
    }
  };

  // Generate employee QR
  const generateEmployeeQR = async () => {
    if (!newEmployee.first_name.trim() || !newEmployee.last_name.trim()) {
      toast.error("Please enter both First Name and Last Name");
      return;
    }

    try {
      const response = await axios.post(`${API}/canteen/qr/generate`, {
        first_name: newEmployee.first_name.trim(),
        last_name: newEmployee.last_name.trim()
      }, { withCredentials: true });

      toast.success("QR code generated");
      setNewEmployeeDialog(false);
      setNewEmployee({ first_name: "", last_name: "" });
      fetchEmployeeQRs();
      setViewQRDialog(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate QR");
    }
  };

  // Parse CSV text into rows of {first_name, last_name}
  const parseBulkCsv = (text) => {
    const rows = [];
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    for (const line of lines) {
      const parts = line.split(/[,;\t]/).map((p) => p.trim());
      if (parts.length < 2) continue;
      const first = parts[0];
      const last = parts[1];
      if (!first || !last) continue;
      // Skip header row
      if (first.toLowerCase().includes("first") && last.toLowerCase().includes("last")) continue;
      rows.push({ first_name: first, last_name: last });
    }
    return rows;
  };

  // Update preview whenever CSV text changes
  useEffect(() => {
    if (bulkDialog) {
      setBulkPreview(parseBulkCsv(bulkCsvText));
    }
  }, [bulkCsvText, bulkDialog]);

  // Handle file upload
  const handleBulkFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      setBulkCsvText(evt.target.result || "");
    };
    reader.readAsText(file);
  };

  // Submit bulk import
  const submitBulkImport = async () => {
    if (bulkPreview.length === 0) {
      toast.error("No valid rows to import");
      return;
    }
    setBulkImporting(true);
    try {
      const response = await axios.post(
        `${API}/canteen/qr/bulk`,
        { employees: bulkPreview },
        { withCredentials: true }
      );
      setBulkResult(response.data);
      toast.success(
        `Imported ${response.data.created_count} employee(s). ${response.data.skipped_count} skipped, ${response.data.error_count} errors.`
      );
      fetchEmployeeQRs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkImporting(false);
    }
  };

  // Reset bulk dialog state
  const closeBulkDialog = () => {
    setBulkDialog(false);
    setBulkCsvText("");
    setBulkPreview([]);
    setBulkResult(null);
  };

  // Guest check-in
  const handleGuestCheckin = async () => {
    if (!guestName) {
      toast.error("Please enter guest name");
      return;
    }
    
    try {
      await axios.post(`${API}/canteen/checkin/guest`, {
        guest_name: guestName,
        meal_type: selectedMealType,
        count: guestCount
      }, { withCredentials: true });
      
      toast.success(`Guest check-in recorded: ${guestCount} for ${selectedMealType}`);
      setGuestDialog(false);
      setGuestName("");
      setGuestCount(1);
      fetchDashboard();
      fetchTodayCheckins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record guest");
    }
  };

  // Generate report
  const generateReport = async () => {
    if (!reportDateRange.start || !reportDateRange.end) {
      toast.error("Please select date range");
      return;
    }
    
    setGeneratingReport(true);
    try {
      const response = await axios.post(`${API}/canteen/report`, {
        report_type: reportType,
        start_date: format(reportDateRange.start, "yyyy-MM-dd"),
        end_date: format(reportDateRange.end, "yyyy-MM-dd")
      }, { withCredentials: true });
      toast.success("Report generated successfully");
      setReportDialogOpen(false);
      setViewingReport(response.data);
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate report");
    } finally {
      setGeneratingReport(false);
    }
  };

  // View report
  const viewReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/canteen/report/${reportId}`, { withCredentials: true });
      setViewingReport(response.data);
    } catch (error) {
      toast.error("Failed to load report");
    }
  };

  // Load employee-level drill-down for a specific meal type inside the viewed report
  const openMealDrillDown = async (meal) => {
    if (!viewingReport) return;
    setDrillDown({ meal: meal.id, label: meal.label, employees: [], guests: [], loading: true });
    try {
      const response = await axios.get(`${API}/canteen/reports/checkins-by-meal`, {
        params: {
          start_date: viewingReport.start_date,
          end_date: viewingReport.end_date,
          meal_type: meal.id,
        },
        withCredentials: true,
      });
      setDrillDown({
        meal: meal.id,
        label: meal.label,
        employees: response.data.employees || [],
        guests: response.data.guests || [],
        loading: false,
      });
    } catch (error) {
      console.error("Drill-down failed", error);
      toast.error("Failed to load employee list");
      setDrillDown(null);
    }
  };

  // Export the currently-loaded meal drill-down to CSV
  const exportDrillDownCsv = () => {
    if (!drillDown || !viewingReport) return;
    const rows = drillDown.employees || [];
    const header = ["Employee Name", "Email", "Department", "Date", "Time"];
    const csvEscape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = rows.map((emp) => {
      const ts = emp.timestamp ? new Date(emp.timestamp) : null;
      return [
        emp.employee_name || "",
        emp.employee_email || "",
        emp.department || "",
        emp.date || "",
        ts ? ts.toLocaleTimeString() : "",
      ].map(csvEscape).join(",");
    });
    const csv = [header.join(","), ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `canteen_${drillDown.meal}_${viewingReport.start_date}_to_${viewingReport.end_date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} employee scan${rows.length === 1 ? "" : "s"}`);
  };

  // Quick date range setters
  const setQuickRange = (type) => {
    const today = new Date();
    switch (type) {
      case "today":
        setReportDateRange({ start: today, end: today });
        setReportType("daily");
        break;
      case "yesterday":
        const yesterday = subDays(today, 1);
        setReportDateRange({ start: yesterday, end: yesterday });
        setReportType("daily");
        break;
      case "thisWeek":
        setReportDateRange({ start: startOfWeek(today, { weekStartsOn: 1 }), end: endOfWeek(today, { weekStartsOn: 1 }) });
        setReportType("weekly");
        break;
      case "thisMonth":
        setReportDateRange({ start: startOfMonth(today), end: endOfMonth(today) });
        setReportType("monthly");
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary" data-testid="canteen-loading">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="canteen-management">
      {/* Header */}
      <header className="glass-header border-b border-black/10 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 md:px-6 h-16">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="back-to-home">
              <ArrowLeft size={18} className="mr-2" />
              Home
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                <ForkKnife size={18} className="text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold">Canteen Management</h1>
                <p className="text-xs text-muted-foreground">QR Check-in System</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{user?.name}</Badge>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-secondary/50 flex-wrap">
            <TabsTrigger value="dashboard" className="gap-2" data-testid="canteen-tab-dashboard">
              <ChartBar size={16} /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="scanner" className="gap-2" data-testid="canteen-tab-scanner">
              <QrCode size={16} /> QR Scanner
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="employees" className="gap-2" data-testid="canteen-tab-employees">
                <Users size={16} /> Employee QRs
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="indent" className="gap-2" data-testid="canteen-tab-indent">
                <CalendarBlank size={16} /> Daily Indent
              </TabsTrigger>
            )}
            <TabsTrigger value="counts" className="gap-2" data-testid="canteen-tab-counts">
              <Users size={16} /> Manual Counts
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-2" data-testid="canteen-tab-reports">
              <FileText size={16} /> Reports
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {MEAL_TYPES.map((meal) => {
                const Icon = meal.icon;
                const todayCount = dashboard?.today_counts?.[meal.id] || { employee: 0, guest: 0 };
                const total = (todayCount.employee || 0) + (todayCount.guest || 0);
                return (
                  <Card key={meal.id} data-testid={`dashboard-card-${meal.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-medium text-muted-foreground">{meal.label}</CardTitle>
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${meal.color}`}>
                          <Icon size={18} />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">{meal.time}</p>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{total}</div>
                      <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                        <span>Emp: {todayCount.employee || 0}</span>
                        <span>•</span>
                        <span>Guest: {todayCount.guest || 0}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Week Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>This Week</CardTitle>
                  <CardDescription>Total meals served this week</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-8">
                    <div>
                      <p className="text-4xl font-bold text-primary">
                        {(dashboard?.week_totals?.employee || 0) + (dashboard?.week_totals?.guest || 0)}
                      </p>
                      <p className="text-sm text-muted-foreground">Total Meals</p>
                    </div>
                    <div className="flex-1 grid grid-cols-2 gap-4">
                      <div className="p-3 bg-blue-50 rounded-md">
                        <p className="text-2xl font-bold text-blue-700">{dashboard?.week_totals?.employee || 0}</p>
                        <p className="text-xs text-blue-600">Employees</p>
                      </div>
                      <div className="p-3 bg-purple-50 rounded-md">
                        <p className="text-2xl font-bold text-purple-700">{dashboard?.week_totals?.guest || 0}</p>
                        <p className="text-xs text-purple-600">Guests</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Today's Indent */}
              <Card>
                <CardHeader>
                  <CardTitle>Today's Indent</CardTitle>
                  <CardDescription>Expected meal counts for today</CardDescription>
                </CardHeader>
                <CardContent>
                  {dashboard?.today_indent ? (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center p-2 bg-secondary rounded">
                        <span className="text-sm">Checked-in Employees</span>
                        <Badge>{dashboard.today_indent.employee_checkin_count}</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {MEAL_TYPES.map((meal) => {
                          const est = dashboard.today_indent.estimates?.[meal.id] || { employee: 0, guest: 0 };
                          return (
                            <div key={meal.id} className="p-2 bg-secondary/50 rounded text-sm">
                              <span className="font-medium">{meal.label}:</span> {est.employee + est.guest}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">No indent created for today</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* QR Scanner Tab */}
          <TabsContent value="scanner">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Scanner */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Camera size={20} />
                        QR Scanner
                      </CardTitle>
                      <CardDescription>Scan employee QR badges</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Select value={selectedMealType} onValueChange={setSelectedMealType}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MEAL_TYPES.map((meal) => (
                            <SelectItem key={meal.id} value={meal.id}>{meal.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {!scannerActive ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <QrCode size={40} className="text-orange-600" />
                      </div>
                      <h3 className="font-semibold mb-2">Ready to Scan</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Select meal type and start scanning employee QR badges
                      </p>
                      <div className="flex gap-2 justify-center">
                        <Button onClick={() => setScannerActive(true)} data-testid="start-scanner-btn">
                          <Camera size={16} className="mr-2" />
                          Start Scanner
                        </Button>
                        <Button variant="outline" onClick={() => setGuestDialog(true)} data-testid="guest-checkin-btn">
                          <UserPlus size={16} className="mr-2" />
                          Guest
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div id="qr-reader" className="mb-4" />
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => {
                          setScannerActive(false);
                          if (scannerRef.current) {
                            scannerRef.current.clear().catch(console.error);
                            scannerRef.current = null;
                          }
                        }}>
                          Stop Scanner
                        </Button>
                        <Button variant="outline" onClick={() => setGuestDialog(true)}>
                          <UserPlus size={16} className="mr-2" />
                          Guest Check-in
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Last Check-in Result */}
                  {lastCheckin && (
                    <div className={`mt-4 p-4 rounded-md ${lastCheckin.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-center gap-2">
                        {lastCheckin.success ? (
                          <CheckCircle size={20} className="text-green-600" />
                        ) : (
                          <Warning size={20} className="text-red-600" />
                        )}
                        <span className={lastCheckin.success ? 'text-green-700' : 'text-red-700'}>
                          {lastCheckin.message}
                        </span>
                      </div>
                      {lastCheckin.employee && (
                        <div className="mt-2 text-sm text-green-600">
                          {lastCheckin.employee.employee_name} • {lastCheckin.employee.department}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Today's Check-ins */}
              <Card>
                <CardHeader>
                  <CardTitle>Today's Check-ins</CardTitle>
                  <CardDescription>{format(new Date(), "PPPP")}</CardDescription>
                </CardHeader>
                <CardContent>
                  {todayCheckins ? (
                    <div className="space-y-4">
                      {MEAL_TYPES.map((meal) => {
                        const Icon = meal.icon;
                        const checkins = todayCheckins.employee_checkins?.[meal.id] || [];
                        const summary = todayCheckins.summary?.[meal.id] || { employees: 0, guests: 0 };
                        return (
                          <div key={meal.id} className="border rounded-md p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className={`w-6 h-6 rounded flex items-center justify-center ${meal.color}`}>
                                  <Icon size={14} />
                                </div>
                                <span className="font-medium">{meal.label}</span>
                              </div>
                              <div className="flex gap-2">
                                <Badge variant="secondary">{summary.employees} emp</Badge>
                                <Badge variant="outline">{summary.guests} guest</Badge>
                              </div>
                            </div>
                            {checkins.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {checkins.slice(0, 5).map((c, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    {c.employee_name}
                                  </Badge>
                                ))}
                                {checkins.length > 5 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{checkins.length - 5} more
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">Loading...</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Employee QRs Tab */}
          {isAdmin && (
            <TabsContent value="employees">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Employee QR Codes</h2>
                  <p className="text-sm text-muted-foreground">Generated automatically for every user. Manage users in the Admin Panel.</p>
                </div>
              </div>

              <Card>
                <CardContent className="pt-4">
                  {employeeQRs.length > 0 ? (
                    <>
                      <div className="relative mb-3">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          value={employeeQRsSearch}
                          onChange={(e) => setEmployeeQRsSearch(e.target.value)}
                          placeholder={`Search ${employeeQRs.length} employees by name, email, department or QR code...`}
                          className="pl-9"
                          data-testid="employee-qrs-search"
                        />
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Department</TableHead>
                            <TableHead>QR Code</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredEmployeeQRs.length === 0 ? (
                            <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-4">No matches for "{employeeQRsSearch}"</TableCell></TableRow>
                          ) : filteredEmployeeQRs.map((emp) => (
                            <TableRow key={emp.qr_id} data-testid={`employee-row-${emp.qr_id}`}>
                              <TableCell className="font-medium">{emp.employee_name || "—"}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{emp.employee_email || "—"}</TableCell>
                              <TableCell>{emp.department ? <Badge variant="outline">{emp.department}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                              <TableCell><code className="text-xs bg-secondary px-2 py-1 rounded">{emp.qr_code}</code></TableCell>
                              <TableCell className="text-right">
                                <Button variant="outline" size="sm" onClick={() => setViewQRDialog(emp)} data-testid={`view-qr-${emp.qr_id}`}>
                                  <QrCode size={16} className="mr-1" />
                                  View QR
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No users yet. Add them via the Admin Panel.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Daily Indent Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="indent">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Daily Indent</CardTitle>
                      <CardDescription>Set estimated meal counts for employees and guests</CardDescription>
                    </div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" data-testid="indent-date-picker">
                          <CalendarBlank size={16} className="mr-2" />
                          {format(selectedDate, "PPP")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={(date) => date && setSelectedDate(date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label>Employee Check-in Count</Label>
                    <Input
                      type="number"
                      value={indent.employee_checkin_count}
                      onChange={(e) => setIndent({ ...indent, employee_checkin_count: parseInt(e.target.value) || 0 })}
                      className="max-w-xs"
                      data-testid="indent-checkin-count"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {MEAL_TYPES.map((meal) => {
                      const Icon = meal.icon;
                      return (
                        <div key={meal.id} className="p-4 border rounded-md">
                          <div className="flex items-center gap-2 mb-4">
                            <div className={`w-8 h-8 rounded-md flex items-center justify-center ${meal.color}`}>
                              <Icon size={18} />
                            </div>
                            <h3 className="font-semibold">{meal.label}</h3>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs">Employees</Label>
                              <Input
                                type="number"
                                value={indent.estimates[meal.id]?.employee || 0}
                                onChange={(e) => setIndent({
                                  ...indent,
                                  estimates: {
                                    ...indent.estimates,
                                    [meal.id]: {
                                      ...indent.estimates[meal.id],
                                      employee: parseInt(e.target.value) || 0
                                    }
                                  }
                                })}
                                data-testid={`indent-${meal.id}-employee`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Guests</Label>
                              <Input
                                type="number"
                                value={indent.estimates[meal.id]?.guest || 0}
                                onChange={(e) => setIndent({
                                  ...indent,
                                  estimates: {
                                    ...indent.estimates,
                                    [meal.id]: {
                                      ...indent.estimates[meal.id],
                                      guest: parseInt(e.target.value) || 0
                                    }
                                  }
                                })}
                                data-testid={`indent-${meal.id}-guest`}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <Button onClick={saveIndent} data-testid="save-indent-btn">
                    Save Indent for {format(selectedDate, "MMM d, yyyy")}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Manual Counts Tab */}
          <TabsContent value="counts">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Manual Meal Counts</CardTitle>
                    <CardDescription>Override or manually enter counts (fallback when QR unavailable)</CardDescription>
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" data-testid="counts-date-picker">
                        <CalendarBlank size={16} className="mr-2" />
                        {format(selectedDate, "PPP")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {MEAL_TYPES.map((meal) => {
                    const Icon = meal.icon;
                    const mealCount = counts[meal.id] || { employee_count: 0, guest_count: 0 };
                    return (
                      <div key={meal.id} className="p-4 border rounded-md">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-md flex items-center justify-center ${meal.color}`}>
                              <Icon size={18} />
                            </div>
                            <h3 className="font-semibold">{meal.label}</h3>
                          </div>
                          <Button size="sm" onClick={() => saveMealCount(meal.id)} data-testid={`save-${meal.id}-btn`}>
                            Save
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs">Employees</Label>
                            <Input
                              type="number"
                              value={mealCount.employee_count || mealCount.employee || 0}
                              onChange={(e) => setCounts({
                                ...counts,
                                [meal.id]: {
                                  ...counts[meal.id],
                                  employee_count: parseInt(e.target.value) || 0
                                }
                              })}
                              data-testid={`count-${meal.id}-employee`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Guests</Label>
                            <Input
                              type="number"
                              value={mealCount.guest_count || mealCount.guest || 0}
                              onChange={(e) => setCounts({
                                ...counts,
                                [meal.id]: {
                                  ...counts[meal.id],
                                  guest_count: parseInt(e.target.value) || 0
                                }
                              })}
                              data-testid={`count-${meal.id}-guest`}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Canteen Reports</h2>
              <Button onClick={() => setReportDialogOpen(true)} data-testid="generate-report-btn">
                <Plus size={16} className="mr-2" />
                Generate Report
              </Button>
            </div>

            {viewingReport ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="capitalize">{viewingReport.report_type} Report</CardTitle>
                      <CardDescription>
                        {viewingReport.start_date} to {viewingReport.end_date}
                      </CardDescription>
                    </div>
                    <Button variant="outline" onClick={() => setViewingReport(null)}>
                      Back to List
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-3xl font-bold text-primary">{viewingReport.data.totals.total_meals}</p>
                        <p className="text-sm text-muted-foreground">Total Meals Served</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-3xl font-bold text-blue-600">{viewingReport.data.totals.total_employee}</p>
                        <p className="text-sm text-muted-foreground">Employee Meals</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-3xl font-bold text-purple-600">{viewingReport.data.totals.total_guest}</p>
                        <p className="text-sm text-muted-foreground">Guest Meals</p>
                      </CardContent>
                    </Card>
                  </div>

                  <h3 className="font-semibold mb-3">Meal Type Breakdown</h3>
                  <p className="text-xs text-muted-foreground mb-2" data-testid="drilldown-hint">
                    Click any meal row to see the employees who scanned in.
                  </p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Meal Type</TableHead>
                        <TableHead className="text-right">Employees</TableHead>
                        <TableHead className="text-right">Guests</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Variance (Emp)</TableHead>
                        <TableHead className="text-right">Variance (Guest)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {MEAL_TYPES.map((meal) => {
                        const emp = viewingReport.data.totals.employee[meal.id] || 0;
                        const guest = viewingReport.data.totals.guest[meal.id] || 0;
                        const varEmp = viewingReport.data.variance[meal.id]?.employee || 0;
                        const varGuest = viewingReport.data.variance[meal.id]?.guest || 0;
                        return (
                          <TableRow
                            key={meal.id}
                            className="cursor-pointer hover:bg-muted/60"
                            onClick={() => openMealDrillDown(meal)}
                            data-testid={`report-meal-row-${meal.id}`}
                            title={`Click to view employees who scanned for ${meal.label}`}
                          >
                            <TableCell className="font-medium">
                              <span className="underline-offset-2 hover:underline">{meal.label}</span>
                            </TableCell>
                            <TableCell className="text-right">{emp}</TableCell>
                            <TableCell className="text-right">{guest}</TableCell>
                            <TableCell className="text-right font-semibold">{emp + guest}</TableCell>
                            <TableCell className="text-right">
                              <span className={varEmp > 0 ? "text-red-600" : varEmp < 0 ? "text-green-600" : ""}>
                                {varEmp > 0 ? "+" : ""}{varEmp}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className={varGuest > 0 ? "text-red-600" : varGuest < 0 ? "text-green-600" : ""}>
                                {varGuest > 0 ? "+" : ""}{varGuest}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="pt-4">
                  {reports.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Report Type</TableHead>
                          <TableHead>Period</TableHead>
                          <TableHead>Submitted</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reports.map((report) => (
                          <TableRow key={report.report_id} data-testid={`report-row-${report.report_id}`}>
                            <TableCell>
                              <Badge className="capitalize">{report.report_type}</Badge>
                            </TableCell>
                            <TableCell>{report.start_date} to {report.end_date}</TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {new Date(report.submitted_at).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" onClick={() => viewReport(report.report_id)}>
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No reports generated yet</p>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Meal Drill-Down Dialog */}
      <Dialog open={!!drillDown} onOpenChange={(open) => { if (!open) setDrillDown(null); }}>
        <DialogContent className="max-w-2xl" data-testid="meal-drilldown-dialog">
          <DialogHeader>
            <DialogTitle>
              {drillDown?.label} — Employees Scanned
            </DialogTitle>
            <DialogDescription>
              {viewingReport ? (
                <>Period: {viewingReport.start_date} to {viewingReport.end_date}</>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          {drillDown?.loading ? (
            <p className="text-center py-6 text-muted-foreground">Loading…</p>
          ) : (
            <div className="space-y-4">
              <div className="flex gap-2 text-sm">
                <Badge variant="secondary" data-testid="drilldown-emp-count">
                  Employees: {drillDown?.employees?.length || 0}
                </Badge>
                <Badge variant="secondary" data-testid="drilldown-guest-count">
                  Guests: {drillDown?.guests?.reduce((s, g) => s + (g.count || 0), 0) || 0}
                </Badge>
              </div>

              <ScrollArea className="max-h-[420px] pr-3">
                {drillDown?.employees?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {drillDown.employees.map((emp, idx) => {
                        const ts = emp.timestamp ? new Date(emp.timestamp) : null;
                        return (
                          <TableRow key={`${emp.employee_email || emp.employee_name || "row"}-${idx}`}>
                            <TableCell>
                              <div className="font-medium">{emp.employee_name || "—"}</div>
                              {emp.employee_email ? (
                                <div className="text-xs text-muted-foreground">{emp.employee_email}</div>
                              ) : null}
                            </TableCell>
                            <TableCell>{emp.department || "—"}</TableCell>
                            <TableCell>{emp.date || "—"}</TableCell>
                            <TableCell>{ts ? ts.toLocaleTimeString() : "—"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-center text-muted-foreground py-6">
                    No employees scanned for {drillDown?.label} in this period.
                  </p>
                )}
              </ScrollArea>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={exportDrillDownCsv}
              disabled={!drillDown || drillDown.loading || !(drillDown?.employees?.length)}
              data-testid="drilldown-export-csv-btn"
            >
              Download CSV
            </Button>
            <Button variant="outline" onClick={() => setDrillDown(null)} data-testid="drilldown-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Report</DialogTitle>
            <DialogDescription>Select report type and date range</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Report Type</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => setQuickRange("today")}>Today</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickRange("yesterday")}>Yesterday</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickRange("thisWeek")}>This Week</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickRange("thisMonth")}>This Month</Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      {reportDateRange.start ? format(reportDateRange.start, "PPP") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={reportDateRange.start}
                      onSelect={(date) => setReportDateRange({ ...reportDateRange, start: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      {reportDateRange.end ? format(reportDateRange.end, "PPP") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={reportDateRange.end}
                      onSelect={(date) => setReportDateRange({ ...reportDateRange, end: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialogOpen(false)}>Cancel</Button>
            <Button onClick={generateReport} disabled={generatingReport} data-testid="confirm-generate-report">
              {generatingReport ? "Generating..." : "Generate Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Employee QR Dialog */}
      <Dialog open={newEmployeeDialog} onOpenChange={setNewEmployeeDialog}>
        <DialogContent data-testid="new-employee-dialog">
          <DialogHeader>
            <DialogTitle>Generate Employee QR Code</DialogTitle>
            <DialogDescription>Enter the employee's First Name and Last Name to generate a unique QR badge</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>First Name</Label>
              <Input
                value={newEmployee.first_name}
                onChange={(e) => setNewEmployee({ ...newEmployee, first_name: e.target.value })}
                placeholder="e.g. Prajwal"
                data-testid="new-employee-first-name"
              />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input
                value={newEmployee.last_name}
                onChange={(e) => setNewEmployee({ ...newEmployee, last_name: e.target.value })}
                placeholder="e.g. Kulkarni"
                data-testid="new-employee-last-name"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNewEmployeeDialog(false)}>Cancel</Button>
            <Button onClick={generateEmployeeQR} data-testid="confirm-generate-qr">
              Generate QR
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import CSV Dialog */}
      <Dialog open={bulkDialog} onOpenChange={(open) => !open && closeBulkDialog()}>
        <DialogContent className="sm:max-w-2xl" data-testid="bulk-import-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Import Employees</DialogTitle>
            <DialogDescription>
              Paste CSV text or upload a .csv file. Each row: <code>First Name, Last Name</code>. Duplicates (by full name) are skipped automatically.
            </DialogDescription>
          </DialogHeader>

          {!bulkResult ? (
            <div className="space-y-4">
              <div>
                <Label>Upload CSV file</Label>
                <Input
                  type="file"
                  accept=".csv,text/csv,text/plain"
                  onChange={handleBulkFileUpload}
                  data-testid="bulk-file-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Or paste CSV content below. Header row optional.
                </p>
              </div>

              <div>
                <Label>CSV Content</Label>
                <Textarea
                  value={bulkCsvText}
                  onChange={(e) => setBulkCsvText(e.target.value)}
                  placeholder={"First Name, Last Name\nPrajwal, Kulkarni\nSneha, Patil\nRahul, Sharma"}
                  rows={8}
                  className="font-mono text-sm"
                  data-testid="bulk-csv-textarea"
                />
              </div>

              {bulkPreview.length > 0 && (
                <div>
                  <Label>Preview ({bulkPreview.length} valid rows)</Label>
                  <ScrollArea className="h-40 border rounded-md p-2">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>#</TableHead>
                          <TableHead>First Name</TableHead>
                          <TableHead>Last Name</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bulkPreview.map((row, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                            <TableCell>{row.first_name}</TableCell>
                            <TableCell>{row.last_name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4" data-testid="bulk-import-result">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-green-50 border border-green-200 rounded">
                  <p className="text-2xl font-bold text-green-700">{bulkResult.created_count}</p>
                  <p className="text-xs text-green-600">Created</p>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-2xl font-bold text-amber-700">{bulkResult.skipped_count}</p>
                  <p className="text-xs text-amber-600">Skipped (duplicates)</p>
                </div>
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-2xl font-bold text-red-700">{bulkResult.error_count}</p>
                  <p className="text-xs text-red-600">Errors</p>
                </div>
              </div>

              {bulkResult.created?.length > 0 && (
                <div>
                  <Label>Newly Created QR Codes</Label>
                  <ScrollArea className="h-48 border rounded-md p-2">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>First Name</TableHead>
                          <TableHead>Last Name</TableHead>
                          <TableHead>QR Code</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bulkResult.created.map((emp) => (
                          <TableRow key={emp.qr_id}>
                            <TableCell>{emp.first_name}</TableCell>
                            <TableCell>{emp.last_name}</TableCell>
                            <TableCell>
                              <code className="text-xs bg-secondary px-2 py-1 rounded">{emp.qr_code}</code>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </div>
              )}

              {bulkResult.skipped?.length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-amber-700">
                    Skipped rows ({bulkResult.skipped.length})
                  </summary>
                  <div className="mt-2 space-y-1">
                    {bulkResult.skipped.map((s, i) => (
                      <p key={i} className="text-xs text-muted-foreground">
                        {s.first_name} {s.last_name} — {s.reason} ({s.qr_code})
                      </p>
                    ))}
                  </div>
                </details>
              )}

              {bulkResult.errors?.length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-red-700">
                    Errors ({bulkResult.errors.length})
                  </summary>
                  <div className="mt-2 space-y-1">
                    {bulkResult.errors.map((er, i) => (
                      <p key={i} className="text-xs text-muted-foreground">
                        Row {er.row}: {er.reason}
                      </p>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          <DialogFooter>
            {!bulkResult ? (
              <>
                <Button variant="outline" onClick={closeBulkDialog}>Cancel</Button>
                <Button
                  onClick={submitBulkImport}
                  disabled={bulkPreview.length === 0 || bulkImporting}
                  data-testid="confirm-bulk-import"
                >
                  {bulkImporting ? "Importing..." : `Import ${bulkPreview.length} Employee${bulkPreview.length === 1 ? "" : "s"}`}
                </Button>
              </>
            ) : (
              <Button onClick={closeBulkDialog} data-testid="bulk-done-btn">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View QR Dialog */}
      <Dialog open={!!viewQRDialog} onOpenChange={() => setViewQRDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="view-qr-dialog">
          <DialogHeader>
            <DialogTitle>Employee QR Badge</DialogTitle>
            <DialogDescription>
              {viewQRDialog?.employee_name || `${viewQRDialog?.first_name || ""} ${viewQRDialog?.last_name || ""}`.trim()}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center py-4">
            {viewQRDialog && (
              <>
                <div className="bg-white p-4 rounded-lg shadow-lg border">
                  <QRCodeSVG
                    value={viewQRDialog.qr_code}
                    size={200}
                    level="H"
                    includeMargin
                  />
                </div>
                <p className="mt-4 font-mono text-lg font-bold">{viewQRDialog.qr_code}</p>
                <p className="text-sm text-muted-foreground">
                  {viewQRDialog.employee_name || `${viewQRDialog.first_name || ""} ${viewQRDialog.last_name || ""}`.trim()}
                </p>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewQRDialog(null)}>Close</Button>
            <Button onClick={() => {
              const fullName = viewQRDialog?.employee_name || `${viewQRDialog?.first_name || ""} ${viewQRDialog?.last_name || ""}`.trim();
              const qrCode = viewQRDialog?.qr_code || "";
              const printWindow = window.open('', '_blank');
              // Escape all user-supplied values before interpolating them into HTML / JS.
              printWindow.document.write(`
                <html>
                  <head><title>QR Badge - ${esc(fullName)}</title></head>
                  <body style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;">
                    <h2>${esc(fullName)}</h2>
                    <div id="qr"></div>
                    <p style="font-family:monospace;font-size:18px;font-weight:bold;">${esc(qrCode)}</p>
                    <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.1/build/qrcode.min.js"></script>
                    <script>
                      QRCode.toCanvas(document.createElement('canvas'), ${JSON.stringify(qrCode)}, { width: 200 }, function(error, canvas) {
                        document.getElementById('qr').appendChild(canvas);
                        setTimeout(() => window.print(), 500);
                      });
                    </script>
                  </body>
                </html>
              `);
            }} data-testid="print-qr-btn">
              Print Badge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guest Check-in Dialog */}
      <Dialog open={guestDialog} onOpenChange={setGuestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Guest Check-in</DialogTitle>
            <DialogDescription>Record guest meal for {selectedMealType}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Guest Name / Company</Label>
              <Input
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Guest name or company"
              />
            </div>
            <div>
              <Label>Number of Guests</Label>
              <Input
                type="number"
                value={guestCount}
                onChange={(e) => setGuestCount(parseInt(e.target.value) || 1)}
                min={1}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setGuestDialog(false)}>Cancel</Button>
            <Button onClick={handleGuestCheckin}>
              Record Guest
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
